// ============================================================
// TEXT INPUT SCENE
// Overlay scene for toki pona text input using glyph selection
// ============================================================

import Phaser from 'phaser';
import {
    GLYPH_COLS,
    GLYPH_ROWS,
    GLYPH_SIZE,
    GLYPH_BUFFER,
    SPECIAL_GLYPHS,
    getGlyphIndex,
    getWord,
    getGlyphCount,
    getGlyphColor,
} from '../toki/TokiPonaData';
import { VOICE_PROFILES } from '../toki/TokiPonaAudio';
import type { TokiPonaAudio, AudioChannel, AudioProfile } from '../toki/TokiPonaAudio';
import type { TouchControls } from '../input/TouchControls';

// Message history
const MAX_HISTORY = 10;

// Layout constants
const ENTRY_COLS = 7;
const ENTRY_ROWS = 2;
const ENTRY_MAX = ENTRY_COLS * ENTRY_ROWS; // 14 words max

const BUTTON_SIZE = 16; // Enlarged special buttons (2x glyph size)
const PADDING = 4;

// Voice profile keys sorted from highest to lowest effective pitch
// effectivePitch = playbackRate * 2^(detune/1200)
const VOICE_KEYS_SORTED: string[] = Object.keys(VOICE_PROFILES).sort((a, b) => {
    const pa = VOICE_PROFILES[a], pb = VOICE_PROFILES[b];
    const pitchA = pa.playbackRate * Math.pow(2, pa.detune / 1200);
    const pitchB = pb.playbackRate * Math.pow(2, pb.detune / 1200);
    return pitchB - pitchA; // high → low
});

export class TextInputScene extends Phaser.Scene {
    // Sent message history (persists across scene launches)
    private static messageHistory: number[][] = [];

    /** Add a message to the history buffer (used by tile click speech) */
    static addToHistory(words: number[]): void {
        TextInputScene.messageHistory.unshift([...words]);
        if (TextInputScene.messageHistory.length > MAX_HISTORY) {
            TextInputScene.messageHistory.length = MAX_HISTORY;
        }
    }

    // Glyph spritesheet frames
    private glyphFrames: Phaser.GameObjects.Image[] = [];

    // Entry display (7x2 grid of slots)
    private entrySlots: Phaser.GameObjects.Image[] = [];
    private entryBorders: Phaser.GameObjects.Rectangle[] = [];
    private entryContent: (number | null)[] = []; // Glyph indices or null for empty
    private cursorIndex: number = 0;

    // Blinking cursor
    private cursorBlink: boolean = true;
    private blinkTimer: Phaser.Time.TimerEvent | null = null;

    // Special buttons
    private enterButton!: Phaser.GameObjects.Image;
    private backspaceButton!: Phaser.GameObjects.Image;
    private escapeButton!: Phaser.GameObjects.Image;

    // Voice profile selector
    private voiceButtons: Phaser.GameObjects.Image[] = [];
    private voiceHighlights: Phaser.GameObjects.Rectangle[] = [];
    private selectedVoiceKey: string = 'walo';

    // Audio preview
    private tokiAudio: TokiPonaAudio | null = null;
    private previewChannel: AudioChannel | null = null;

    // Background
    private overlay!: Phaser.GameObjects.Rectangle;

    // Keyboard
    private escKey!: Phaser.Input.Keyboard.Key;
    private enterKey!: Phaser.Input.Keyboard.Key;
    private backspaceKey!: Phaser.Input.Keyboard.Key;
    private upKey!: Phaser.Input.Keyboard.Key;
    private downKey!: Phaser.Input.Keyboard.Key;

    // Message history navigation
    // -1 = composing new message, 0 = most recent history, 1 = second most recent, etc.
    private historyIndex: number = -1;
    private draftContent: (number | null)[] = []; // Saved draft when browsing history
    private draftCursorIndex: number = 0;

    // Mobile touch history navigation
    private touchControls: TouchControls | null = null;
    private lastTouchUp: boolean = false;
    private lastTouchDown: boolean = false;

    // Dictionary overlay
    private dKey!: Phaser.Input.Keyboard.Key;
    private dictionaryOpen: boolean = false;
    private dictionaryButton!: Phaser.GameObjects.Image;

    // Mobile layout mode (wider glyph grid, voice row below grid)
    private isMobileLayout: boolean = false;

    // Callback for when message is confirmed
    private onMessageSend: ((words: number[], voice: string) => void) | null = null;
    private onClose: (() => void) | null = null;

    constructor() {
        super({ key: 'TextInputScene' });
    }

    init(data: {
        onMessageSend?: (words: number[], voice: string) => void;
        selectedVoice?: string;
        audio?: TokiPonaAudio;
        onClose?: () => void;
        touchControls?: TouchControls;
    }): void {
        this.onMessageSend = data.onMessageSend || null;
        this.onClose = data.onClose || null;
        this.tokiAudio = data.audio || null;
        this.touchControls = data.touchControls || null;
        this.lastTouchUp = false;
        this.lastTouchDown = false;
        if (data.selectedVoice && data.selectedVoice in VOICE_PROFILES) {
            this.selectedVoiceKey = data.selectedVoice;
        }
        // Reset history navigation
        this.historyIndex = -1;
        this.draftContent = [];
        this.draftCursorIndex = 0;
        // Create a preview channel for the current voice
        this.updatePreviewChannel();
    }

    preload(): void {
        // Glyph spritesheet 'toki_glyphs' is already processed by GameScene
        // (white bg dissolved, black ink → white). No loading needed here.
    }

    create(): void {
        this.isMobileLayout = this.touchControls?.isEnabled() ?? false;

        const { width, height } = this.cameras.main;

        // Reset arrays — scene instance is reused across stop/launch cycles,
        // so stale references to destroyed game objects must be cleared.
        this.glyphFrames = [];
        this.entrySlots = [];
        this.entryBorders = [];
        this.voiceButtons = [];
        this.voiceHighlights = [];

        // Semi-transparent overlay
        this.overlay = this.add.rectangle(0, 0, width, height, 0x000000, 0.7);
        this.overlay.setOrigin(0, 0);
        this.overlay.setDepth(0);

        // Setup keyboard
        this.escKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
        this.enterKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER);
        this.backspaceKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.BACKSPACE);
        this.upKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
        this.downKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN);
        this.dKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.D);

        // Initialize entry content
        this.entryContent = new Array(ENTRY_MAX).fill(null);
        this.cursorIndex = 0;

        // Create UI elements
        this.createEntryArea();
        this.createSpecialButtons();
        this.createGlyphGrid();
        this.createVoiceSelector();
        this.createDictionaryButton();

        // Start cursor blink
        this.blinkTimer = this.time.addEvent({
            delay: 400,
            callback: () => {
                this.cursorBlink = !this.cursorBlink;
                this.updateCursor();
            },
            loop: true,
        });

        // Initial cursor update
        this.updateCursor();
    }

    private updatePreviewChannel(): void {
        if (this.previewChannel) this.previewChannel.stop();
        const profile = VOICE_PROFILES[this.selectedVoiceKey];
        this.previewChannel = this.tokiAudio?.createChannel(profile) ?? null;
    }

    private playPreview(word: string, profile?: AudioProfile): void {
        if (!this.tokiAudio) return;
        // For voice button clicks: play in the clicked voice
        // For glyph clicks: play in the current voice (previewChannel)
        if (profile) {
            const ch = this.tokiAudio.createChannel(profile);
            ch?.play(word, '1.0').catch(() => {});
        } else if (this.previewChannel) {
            this.previewChannel.stop();
            this.previewChannel.play(word, '1.0').catch(() => {});
        }
    }

    private createEntryArea(): void {
        const { width } = this.cameras.main;

        // Entry area at top center
        const entryWidth = ENTRY_COLS * (GLYPH_SIZE + GLYPH_BUFFER) - GLYPH_BUFFER;
        const entryHeight = ENTRY_ROWS * (GLYPH_SIZE + GLYPH_BUFFER) - GLYPH_BUFFER;
        const startX = (width - entryWidth) / 2;
        const startY = PADDING + BUTTON_SIZE + PADDING; // Below escape button

        // Background for entry area
        const entryBg = this.add.rectangle(
            startX - PADDING,
            startY - PADDING,
            entryWidth + PADDING * 2,
            entryHeight + PADDING * 2,
            0x333333
        );
        entryBg.setOrigin(0, 0);
        entryBg.setDepth(1);

        // Create slots
        for (let row = 0; row < ENTRY_ROWS; row++) {
            for (let col = 0; col < ENTRY_COLS; col++) {
                const x = startX + col * (GLYPH_SIZE + GLYPH_BUFFER);
                const y = startY + row * (GLYPH_SIZE + GLYPH_BUFFER);

                // Border/background for slot
                const border = this.add.rectangle(x, y, GLYPH_SIZE, GLYPH_SIZE, 0x666666);
                border.setOrigin(0, 0);
                border.setDepth(2);
                this.entryBorders.push(border);

                // Glyph image (initially invisible)
                const slot = this.add.image(x, y, 'toki_glyphs', 0);
                slot.setOrigin(0, 0);
                slot.setDepth(3);
                slot.setVisible(false);
                this.entrySlots.push(slot);
            }
        }
    }

    private createSpecialButtons(): void {
        const { width } = this.cameras.main;

        // Escape button (ala) - top left
        const alaIndex = getGlyphIndex(SPECIAL_GLYPHS.ESCAPE);
        this.escapeButton = this.add.image(PADDING, PADDING, 'toki_glyphs', alaIndex);
        this.escapeButton.setOrigin(0, 0);
        this.escapeButton.setDisplaySize(BUTTON_SIZE, BUTTON_SIZE);
        this.escapeButton.setDepth(10);
        this.escapeButton.setTint(getGlyphColor(alaIndex));
        this.escapeButton.setInteractive({ useHandCursor: true });
        this.escapeButton.on('pointerdown', () => this.handleEscape());

        // Entry area dimensions for positioning side buttons
        const entryWidth = ENTRY_COLS * (GLYPH_SIZE + GLYPH_BUFFER) - GLYPH_BUFFER;
        const startX = (width - entryWidth) / 2;
        const buttonY = PADDING + BUTTON_SIZE + PADDING + (GLYPH_SIZE + GLYPH_BUFFER) / 2;

        // Backspace button (weka) - left of entry
        const wekaIndex = getGlyphIndex(SPECIAL_GLYPHS.BACKSPACE);
        this.backspaceButton = this.add.image(
            startX - PADDING - BUTTON_SIZE,
            buttonY,
            'toki_glyphs',
            wekaIndex
        );
        this.backspaceButton.setOrigin(0, 0);
        this.backspaceButton.setDisplaySize(BUTTON_SIZE, BUTTON_SIZE);
        this.backspaceButton.setDepth(10);
        this.backspaceButton.setTint(getGlyphColor(wekaIndex));
        this.backspaceButton.setInteractive({ useHandCursor: true });
        this.backspaceButton.on('pointerdown', () => this.handleBackspace());

        // Enter button (pona) - right of entry
        const ponaIndex = getGlyphIndex(SPECIAL_GLYPHS.ENTER);
        this.enterButton = this.add.image(
            startX + entryWidth + PADDING,
            buttonY,
            'toki_glyphs',
            ponaIndex
        );
        this.enterButton.setOrigin(0, 0);
        this.enterButton.setDisplaySize(BUTTON_SIZE, BUTTON_SIZE);
        this.enterButton.setDepth(10);
        this.enterButton.setTint(getGlyphColor(ponaIndex));
        this.enterButton.setInteractive({ useHandCursor: true });
        this.enterButton.on('pointerdown', () => this.handleEnter());
    }

    private createGlyphGrid(): void {
        const { width, height } = this.cameras.main;

        // On mobile, spread glyphs across full viewport width for easier tapping
        const hSpacing = this.isMobileLayout
            ? Math.floor((width - 2 * PADDING) / GLYPH_COLS)
            : GLYPH_SIZE + GLYPH_BUFFER;
        const vSpacing = GLYPH_SIZE + GLYPH_BUFFER;

        // Position below entry area
        const entryHeight = ENTRY_ROWS * vSpacing - GLYPH_BUFFER;
        const gridStartY = PADDING + BUTTON_SIZE + PADDING + entryHeight + PADDING * 3;
        const gridTotalWidth = (GLYPH_COLS - 1) * hSpacing + GLYPH_SIZE;
        const gridStartX = Math.floor((width - gridTotalWidth) / 2);

        // Wider hitbox on mobile to cover the full cell
        const hitWidth = this.isMobileLayout ? hSpacing : 10;

        // Create glyph buttons
        const glyphCount = getGlyphCount();
        for (let i = 0; i < glyphCount; i++) {
            const col = i % GLYPH_COLS;
            const row = Math.floor(i / GLYPH_COLS);
            const x = gridStartX + col * hSpacing;
            const y = gridStartY + row * vSpacing;

            const glyph = this.add.image(x, y, 'toki_glyphs', i);
            glyph.setOrigin(0, 0);
            glyph.setDepth(5);
            glyph.setInteractive(
                new Phaser.Geom.Rectangle(-1, -1, hitWidth, 10),
                Phaser.Geom.Rectangle.Contains,
            );
            glyph.input!.cursor = 'pointer';

            // Apply per-glyph color tint
            const baseColor = getGlyphColor(i);
            glyph.setTint(baseColor);

            // Click to select glyph
            glyph.on('pointerdown', () => this.selectGlyph(i));

            // Hover effect — brighten on hover, restore base color on out
            glyph.on('pointerover', () => glyph.setTint(0xffffff));
            glyph.on('pointerout', () => glyph.setTint(baseColor));

            this.glyphFrames.push(glyph);
        }
    }

    private createVoiceSelector(): void {
        const { width, height } = this.cameras.main;
        const voiceCount = VOICE_KEYS_SORTED.length;
        const voiceSize = GLYPH_SIZE + GLYPH_BUFFER;

        if (this.isMobileLayout) {
            // Mobile: horizontal row at bottom of viewport
            const voiceRowY = height - GLYPH_SIZE - PADDING;

            // Same horizontal spacing as glyph grid (fills viewport width)
            const hSpacing = Math.floor((width - 2 * PADDING) / voiceCount);
            const rowWidth = (voiceCount - 1) * hSpacing + GLYPH_SIZE;
            const startX = Math.floor((width - rowWidth) / 2);

            for (let i = 0; i < voiceCount; i++) {
                const key = VOICE_KEYS_SORTED[i];
                const profile = VOICE_PROFILES[key];
                const glyphIndex = getGlyphIndex(profile.word);
                const x = startX + i * hSpacing;
                const y = voiceRowY;

                const highlight = this.add.rectangle(x - 1, y - 1, GLYPH_SIZE + 2, GLYPH_SIZE + 2, 0xffffff, 0);
                highlight.setOrigin(0, 0);
                highlight.setDepth(4);
                this.voiceHighlights.push(highlight);

                const btn = this.add.image(x, y, 'toki_glyphs', glyphIndex);
                btn.setOrigin(0, 0);
                btn.setDepth(5);
                btn.setInteractive(
                    new Phaser.Geom.Rectangle(-1, -1, hSpacing, 10),
                    Phaser.Geom.Rectangle.Contains,
                );
                btn.input!.cursor = 'pointer';

                const baseColor = getGlyphColor(glyphIndex);
                btn.setTint(key === this.selectedVoiceKey ? 0xffffff : baseColor);

                btn.on('pointerdown', () => this.selectVoice(i));
                btn.on('pointerover', () => {
                    if (VOICE_KEYS_SORTED[i] !== this.selectedVoiceKey) btn.setTint(0xffffff);
                });
                btn.on('pointerout', () => {
                    if (VOICE_KEYS_SORTED[i] !== this.selectedVoiceKey) btn.setTint(getGlyphColor(glyphIndex));
                });

                this.voiceButtons.push(btn);
            }
        } else {
            // Desktop: vertical column on the right, vertically centered
            const columnHeight = voiceCount * voiceSize - GLYPH_BUFFER;
            const colX = width - PADDING - GLYPH_SIZE - PADDING;
            const colY = (height - columnHeight) / 2;

            for (let i = 0; i < voiceCount; i++) {
                const key = VOICE_KEYS_SORTED[i];
                const profile = VOICE_PROFILES[key];
                const glyphIndex = getGlyphIndex(profile.word);
                const x = colX;
                const y = colY + i * voiceSize;

                const highlight = this.add.rectangle(x - 1, y - 1, GLYPH_SIZE + 2, GLYPH_SIZE + 2, 0xffffff, 0);
                highlight.setOrigin(0, 0);
                highlight.setDepth(4);
                this.voiceHighlights.push(highlight);

                const btn = this.add.image(x, y, 'toki_glyphs', glyphIndex);
                btn.setOrigin(0, 0);
                btn.setDepth(5);
                btn.setInteractive({ useHandCursor: true });

                const baseColor = getGlyphColor(glyphIndex);
                btn.setTint(key === this.selectedVoiceKey ? 0xffffff : baseColor);

                btn.on('pointerdown', () => this.selectVoice(i));
                btn.on('pointerover', () => {
                    if (VOICE_KEYS_SORTED[i] !== this.selectedVoiceKey) btn.setTint(0xffffff);
                });
                btn.on('pointerout', () => {
                    if (VOICE_KEYS_SORTED[i] !== this.selectedVoiceKey) btn.setTint(getGlyphColor(glyphIndex));
                });

                this.voiceButtons.push(btn);
            }
        }

        // Apply initial selection highlight
        this.updateVoiceSelection();
    }

    private createDictionaryButton(): void {
        const { width } = this.cameras.main;

        // Dictionary button (lipu = paper/document) - top right, mirroring escape button
        const lipuIndex = getGlyphIndex('lipu');
        this.dictionaryButton = this.add.image(width - PADDING - BUTTON_SIZE, PADDING, 'toki_glyphs', lipuIndex);
        this.dictionaryButton.setOrigin(0, 0);
        this.dictionaryButton.setDisplaySize(BUTTON_SIZE, BUTTON_SIZE);
        this.dictionaryButton.setDepth(10);
        this.dictionaryButton.setTint(getGlyphColor(lipuIndex));
        this.dictionaryButton.setInteractive({ useHandCursor: true });
        this.dictionaryButton.on('pointerdown', () => this.openDictionary());
    }

    private openDictionary(): void {
        if (this.dictionaryOpen) return;
        this.dictionaryOpen = true;
        this.scene.launch('DictionaryScene', {
            onClose: () => {
                this.dictionaryOpen = false;
            },
            touchControls: this.touchControls,
        });
    }

    private selectVoice(index: number): void {
        const key = VOICE_KEYS_SORTED[index];
        this.selectedVoiceKey = key;
        this.updateVoiceSelection();
        this.updatePreviewChannel();

        // Play the archetype's word in that voice as a preview
        const profile = VOICE_PROFILES[key];
        this.playPreview(profile.word, profile);
    }

    private updateVoiceSelection(): void {
        for (let i = 0; i < VOICE_KEYS_SORTED.length; i++) {
            const key = VOICE_KEYS_SORTED[i];
            const profile = VOICE_PROFILES[key];
            const glyphIndex = getGlyphIndex(profile.word);
            const isSelected = key === this.selectedVoiceKey;

            // Highlight: white border for selected, invisible for others
            this.voiceHighlights[i].setFillStyle(0xffffff, isSelected ? 0.25 : 0);

            // Glyph: white when selected, colored otherwise
            this.voiceButtons[i].setTint(isSelected ? 0xffffff : getGlyphColor(glyphIndex));
        }
    }

    private selectGlyph(glyphIndex: number): void {
        if (this.cursorIndex >= ENTRY_MAX) return;

        // Set glyph at cursor position with its color
        this.entryContent[this.cursorIndex] = glyphIndex;
        this.entrySlots[this.cursorIndex].setFrame(glyphIndex);
        this.entrySlots[this.cursorIndex].setTint(getGlyphColor(glyphIndex));
        this.entrySlots[this.cursorIndex].setVisible(true);

        // Play the word in the current voice
        const word = getWord(glyphIndex);
        if (word) this.playPreview(word);

        // Move cursor to next position
        if (this.cursorIndex < ENTRY_MAX - 1) {
            this.cursorIndex++;
        }

        this.updateCursor();
    }

    private handleBackspace(): void {
        if (this.cursorIndex === 0 && this.entryContent[0] === null) return;

        // If current slot is empty, move back first
        if (this.entryContent[this.cursorIndex] === null && this.cursorIndex > 0) {
            this.cursorIndex--;
        }

        // Clear current slot
        this.entryContent[this.cursorIndex] = null;
        this.entrySlots[this.cursorIndex].setVisible(false);

        this.updateCursor();
    }

    private handleEnter(): void {
        // Collect non-null entries
        const words: number[] = [];
        for (const content of this.entryContent) {
            if (content !== null) {
                words.push(content);
            }
        }

        if (words.length > 0) {
            // Save to message history (most recent first)
            TextInputScene.messageHistory.unshift([...words]);
            if (TextInputScene.messageHistory.length > MAX_HISTORY) {
                TextInputScene.messageHistory.length = MAX_HISTORY;
            }

            if (this.onMessageSend) {
                this.onMessageSend(words, this.selectedVoiceKey);
            }
        }

        this.closeScene();
    }

    private handleEscape(): void {
        this.closeScene();
    }

    /** Called by GameScene when T button is pressed while this scene is open */
    handleTalkButton(): void {
        const hasContent = this.entryContent.some(c => c !== null);
        if (hasContent) {
            this.handleEnter();
        } else {
            this.handleEscape();
        }
    }

    private handleHistoryUp(): void {
        const history = TextInputScene.messageHistory;
        if (history.length === 0) return;

        // Save draft when leaving new-message mode
        if (this.historyIndex === -1) {
            this.draftContent = [...this.entryContent];
            this.draftCursorIndex = this.cursorIndex;
        }

        // Move to older message
        if (this.historyIndex < history.length - 1) {
            this.historyIndex++;
            this.loadHistoryEntry(history[this.historyIndex]);
        }
    }

    private handleHistoryDown(): void {
        if (this.historyIndex <= -1) return;

        this.historyIndex--;

        if (this.historyIndex === -1) {
            // Restore draft
            this.loadEntryContent(this.draftContent, this.draftCursorIndex);
        } else {
            this.loadHistoryEntry(TextInputScene.messageHistory[this.historyIndex]);
        }
    }

    private loadHistoryEntry(words: number[]): void {
        // Build entry content from history words
        const content: (number | null)[] = new Array(ENTRY_MAX).fill(null);
        for (let i = 0; i < words.length && i < ENTRY_MAX; i++) {
            content[i] = words[i];
        }
        const cursor = Math.min(words.length, ENTRY_MAX - 1);
        this.loadEntryContent(content, cursor);
    }

    private loadEntryContent(content: (number | null)[], cursor: number): void {
        this.entryContent = [...content];
        // Pad to ENTRY_MAX if needed
        while (this.entryContent.length < ENTRY_MAX) {
            this.entryContent.push(null);
        }
        this.cursorIndex = cursor;

        // Update visual slots
        for (let i = 0; i < ENTRY_MAX; i++) {
            const glyphIndex = this.entryContent[i];
            if (glyphIndex !== null) {
                this.entrySlots[i].setFrame(glyphIndex);
                this.entrySlots[i].setTint(getGlyphColor(glyphIndex));
                this.entrySlots[i].setVisible(true);
            } else {
                this.entrySlots[i].setVisible(false);
            }
        }

        this.updateCursor();
    }

    private closeScene(): void {
        // Clean up
        if (this.blinkTimer) {
            this.blinkTimer.destroy();
            this.blinkTimer = null;
        }
        if (this.previewChannel) {
            this.previewChannel.stop();
            this.previewChannel = null;
        }

        // Stop this scene and re-enable game input
        this.onClose?.();
        this.scene.stop();
    }

    private updateCursor(): void {
        // Update all slot borders
        for (let i = 0; i < ENTRY_MAX; i++) {
            const border = this.entryBorders[i];

            if (i === this.cursorIndex) {
                // Cursor position - blink between black and white
                border.setFillStyle(this.cursorBlink ? 0xffffff : 0x000000);
            } else if (this.entryContent[i] !== null) {
                // Filled slot
                border.setFillStyle(0x444444);
            } else {
                // Empty slot
                border.setFillStyle(0x666666);
            }
        }
    }

    update(): void {
        // Suppress all input while dictionary overlay is open
        if (this.dictionaryOpen) return;

        // Handle keyboard input
        if (Phaser.Input.Keyboard.JustDown(this.escKey)) {
            this.handleEscape();
        }
        if (Phaser.Input.Keyboard.JustDown(this.enterKey)) {
            this.handleEnter();
        }
        if (Phaser.Input.Keyboard.JustDown(this.backspaceKey)) {
            this.handleBackspace();
        }
        if (Phaser.Input.Keyboard.JustDown(this.dKey)) {
            this.openDictionary();
        }

        // History navigation — keyboard
        if (Phaser.Input.Keyboard.JustDown(this.upKey)) {
            this.handleHistoryUp();
        }
        if (Phaser.Input.Keyboard.JustDown(this.downKey)) {
            this.handleHistoryDown();
        }

        // History navigation — mobile touch controls
        if (this.touchControls) {
            const touchUp = this.touchControls.state.up;
            const touchDown = this.touchControls.state.down;

            if (touchUp && !this.lastTouchUp) {
                this.handleHistoryUp();
            }
            if (touchDown && !this.lastTouchDown) {
                this.handleHistoryDown();
            }

            this.lastTouchUp = touchUp;
            this.lastTouchDown = touchDown;
        }
    }
}
